import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, TextField, IconButton, Avatar,
  CircularProgress, Card, CardMedia, CardContent, Button, Chip, List,
  ListItemButton, ListItemText, Divider
} from '@mui/material';
import { Send, SmartToy, Person, AttachFile, Close, Add } from '@mui/icons-material';
import api from '../../services/api';

const WELCOME_MESSAGE = {
  role: 'assistant',
  text:
    "Hi, I'm Sam, OSESG's sourcing engineer. Tell me what you're trying to accomplish — the application, " +
    "industry, or problem you need equipment for — and I'll help you find the right products from our " +
    'catalog. You can also attach a photo if that helps explain what you need.',
  products: []
};

const formatDate = (d) => {
  const date = new Date(d);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const ProductCard = ({ product }) => {
  const image = product.images?.find((img) => img.isPrimary) || product.images?.[0];
  return (
    <Card variant="outlined" sx={{ display: 'flex', mb: 1, borderRadius: 2 }}>
      {image?.url && (
        <CardMedia component="img" image={image.url} alt={product.name} sx={{ width: 96, height: 96, objectFit: 'cover' }} />
      )}
      <CardContent sx={{ flex: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="primary" fontWeight={600}>{product.osesgCode}</Typography>
        <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>{product.name}</Typography>
        {product.aiReason && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{product.aiReason}</Typography>
        )}
        <Button component={Link} to={`/products/${product._id}`} size="small" sx={{ mt: 0.5, px: 0, textTransform: 'none' }}>
          View product →
        </Button>
      </CardContent>
    </Card>
  );
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexDirection: isUser ? 'row-reverse' : 'row' }}>
      <Avatar sx={{ bgcolor: isUser ? '#ff6f00' : '#1a237e', width: 32, height: 32 }}>
        {isUser ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
      </Avatar>
      <Box sx={{ maxWidth: '75%' }}>
        {message.imageUrl && (
          <Box component="img" src={message.imageUrl} alt="attached"
            sx={{ maxWidth: 220, maxHeight: 160, borderRadius: 2, display: 'block', mb: 0.5, ml: isUser ? 'auto' : 0 }} />
        )}
        {message.text && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5, borderRadius: 3, whiteSpace: 'pre-wrap',
              bgcolor: isUser ? '#1a237e' : '#f0f2f8',
              color: isUser ? 'white' : 'text.primary'
            }}
          >
            <Typography variant="body2">{message.text}</Typography>
          </Paper>
        )}
        {message.cached && (
          <Chip label="⚡ Instant answer from our knowledge base" size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />
        )}
        {message.products?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {message.products.map((p, i) => <ProductCard key={p._id || i} product={p} />)}
          </Box>
        )}
        {message.filedProductRequests?.length > 0 && (
          <Chip label="📋 Product request filed for our sourcing team" size="small" color="info" variant="outlined" sx={{ mt: 0.5, mr: 0.5 }} />
        )}
        {message.filedCustomizationRequests?.map((r) => (
          <Chip
            key={r.id} component={Link} to={`/customer/customization-requests/${r.id}`} clickable
            label={`🛠️ Customization request filed for ${r.productName} — view & attach photos`}
            size="small" color="secondary" variant="outlined" sx={{ mt: 0.5, mr: 0.5 }}
          />
        ))}
      </Box>
    </Box>
  );
};

const AIAssistantPage = () => {
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [input, setInput] = useState('');
  const [pendingImage, setPendingImage] = useState(null); // { file, previewUrl }
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [budgetExceeded, setBudgetExceeded] = useState(false);
  const [usage, setUsage] = useState(null);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);

  const loadConversations = async () => {
    try {
      const { data } = await api.get('/ai/conversations');
      setConversations(data.data);
    } catch (err) { /* non-fatal — sidebar just stays empty */ }
  };

  const loadUsage = async () => {
    try {
      const { data } = await api.get('/ai/usage');
      setUsage(data.data);
      setBudgetExceeded(data.data.remainingTodayUSD <= 0);
    } catch (err) { /* non-fatal — usage chip just stays hidden */ }
  };

  useEffect(() => { loadConversations(); loadUsage(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([WELCOME_MESSAGE]);
    setError('');
  };

  const openConversation = async (id) => {
    if (id === conversationId) return;
    setLoadingThread(true);
    setError('');
    try {
      const { data } = await api.get(`/ai/conversations/${id}`);
      setConversationId(id);
      setMessages([WELCOME_MESSAGE, ...data.data.messages]);
    } catch (err) {
      setError('Could not load that conversation.');
    } finally {
      setLoadingThread(false);
    }
  };

  const handlePickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingImage({ file, previewUrl: URL.createObjectURL(file) });
    e.target.value = '';
  };

  const clearPendingImage = () => setPendingImage(null);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text && !pendingImage) return;

    setError('');
    setSending(true);

    let imageUrl;
    let attachedPreviewUrl;
    try {
      if (pendingImage) {
        const formData = new FormData();
        formData.append('image', pendingImage.file);
        const { data } = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = `${window.location.origin}${data.data.url}`;
        attachedPreviewUrl = data.data.url;
      }
    } catch (err) {
      setSending(false);
      setError('Could not upload the image. Please try again.');
      return;
    }

    setMessages((prev) => [...prev, { role: 'user', text, imageUrl: attachedPreviewUrl }]);
    setInput('');
    setPendingImage(null);

    try {
      const { data } = await api.post('/ai/assistant', { message: text, imageUrl, conversationId });
      setMessages((prev) => [...prev, {
        role: 'assistant', text: data.data.reply, products: data.data.products, cached: data.data.cached,
        filedProductRequests: data.data.filedProductRequests, filedCustomizationRequests: data.data.filedCustomizationRequests
      }]);
      if (!conversationId) setConversationId(data.data.conversationId);
      loadConversations();
    } catch (err) {
      if (err.response?.data?.code === 'AI_BUDGET_EXCEEDED') {
        setBudgetExceeded(true);
        setError(err.response.data.error);
        setMessages((prev) => [...prev, { role: 'assistant', text: err.response.data.error }]);
      } else {
        setError(err.response?.data?.error || 'Something went wrong. Please try again.');
        setMessages((prev) => [...prev, {
          role: 'assistant',
          text: 'Sorry, I ran into an issue answering that. Could you try rephrasing, or try again in a moment?'
        }]);
      }
    } finally {
      setSending(false);
      loadUsage();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 1.5, textAlign: 'center', flexShrink: 0 }}>
        <Typography variant="h5" fontWeight={800} color="#1a237e">AI Sourcing Assistant</Typography>
        <Typography variant="body2" color="text.secondary">
          Describe what you need, or attach a photo — Sam will find matching products and explain why they fit.
        </Typography>
        {usage && (
          <Chip
            size="small"
            variant="outlined"
            color={usage.remainingTodayUSD > 0 ? 'default' : 'warning'}
            label={
              usage.freeDaysUsed < usage.freeDaysLimit && !usage.isManualOverride
                ? `Free trial · ${usage.freeDaysLimit - usage.freeDaysUsed} usage-days left · $${usage.remainingTodayUSD.toFixed(2)} left today`
                : `$${usage.remainingTodayUSD.toFixed(2)} left today`
            }
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Conversation list */}
        <Paper variant="outlined" sx={{ width: 260, flexShrink: 0, borderRadius: 3, overflow: 'auto', display: { xs: 'none', sm: 'block' } }}>
          <Box sx={{ p: 1.5 }}>
            <Button fullWidth variant="outlined" startIcon={<Add />} onClick={startNewConversation} sx={{ textTransform: 'none', borderRadius: 2 }}>
              New conversation
            </Button>
          </Box>
          <Divider />
          <List dense disablePadding>
            {conversations.map((c) => (
              <ListItemButton key={c._id} selected={c._id === conversationId} onClick={() => openConversation(c._id)}>
                <ListItemText
                  primary={c.title}
                  secondary={formatDate(c.updatedAt)}
                  primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem', fontWeight: c._id === conversationId ? 700 : 400 }}
                  secondaryTypographyProps={{ fontSize: '0.7rem' }}
                />
              </ListItemButton>
            ))}
            {conversations.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
                Your past conversations will appear here.
              </Typography>
            )}
          </List>
        </Paper>

        {/* Chat area */}
        <Paper variant="outlined" sx={{ flex: 1, borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#fafafa' }}>
            {loadingThread ? (
              <Box display="flex" justifyContent="center" pt={4}><CircularProgress size={24} /></Box>
            ) : (
              messages.map((m, i) => <MessageBubble key={i} message={m} />)
            )}
            {sending && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', ml: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">Sam is thinking…</Typography>
              </Box>
            )}
            <div ref={endRef} />
          </Box>

          <Box sx={{ borderTop: '1px solid #e0e0e0', bgcolor: 'white' }}>
            {error && <Typography variant="caption" color="error" sx={{ px: 2, pt: 1, display: 'block' }}>{error}</Typography>}
            {pendingImage && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1 }}>
                <Chip
                  avatar={<Avatar src={pendingImage.previewUrl} variant="rounded" />}
                  label={pendingImage.file.name}
                  onDelete={clearPendingImage}
                  deleteIcon={<Close />}
                />
              </Box>
            )}
            <Box component="form" onSubmit={handleSend} sx={{ display: 'flex', gap: 1, p: 1.5, alignItems: 'flex-end' }}>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePickImage} />
              <IconButton onClick={() => fileInputRef.current?.click()} disabled={sending || budgetExceeded} aria-label="Attach image">
                <AttachFile />
              </IconButton>
              <TextField
                fullWidth multiline maxRows={4} size="small"
                placeholder={budgetExceeded ? "Today's allowance is used up — try again tomorrow" : 'Describe what you need...'}
                value={input} onChange={(e) => setInput(e.target.value)} disabled={sending || budgetExceeded}
              />
              <IconButton type="submit" color="primary" disabled={sending || budgetExceeded || (!input.trim() && !pendingImage)}>
                <Send />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AIAssistantPage;
